import { Platform } from 'react-native'

import { resetNavigation } from '@zet/common/utils/zetNavigation'
import useAuthStore from '@zet/feature-user/store/authStore'
import { getAppVersionNo, isApp } from '@zet/utils'
import { getUserToken } from '@zet/utils/userToken'
import axios from 'axios'

import { logError } from './utils/logError'
import { refreshToken } from './utils/refreshToken'

const zetApi = axios.create()

let isRefreshTokenFetchTried = false

let apiURL: string
// check for dsa-dashboard env var
if (process.env.NEXT_PUBLIC_API_END_POINT) {
  apiURL = process.env.NEXT_PUBLIC_API_END_POINT
} else {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { env } = require('@env')
  apiURL = env.API_BASE_URL
}

zetApi.defaults.baseURL = apiURL

//* All request will wait 3 seconds before timeout
zetApi.defaults.timeout = 30000
zetApi.defaults.headers.common = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
  appVersionNo: getAppVersionNo(),
  locale: 'en',
  ...(Platform.OS !== 'web' && { appOS: Platform.OS }),
}
zetApi.defaults.withCredentials = false

//* Request interceptor
zetApi.interceptors.request.use(
  function (request) {
    const controller = new AbortController()
    request.signal = controller.signal
    if (
      isRefreshTokenFetchTried &&
      !request.url?.includes('user/refreshToken')
    ) {
      controller.abort()
    }
    return request
  },
  function (error) {
    return Promise.reject(error)
  },
)

const string_chop = function (str = '', size = 255) {
  if (str === '') return []
  str = String(str)
  const regExp = str.match(new RegExp('.{1,' + size + '}', 'g'))
  return size > 0 && regExp ? regExp : [str]
}

//* Response interceptor
zetApi.interceptors.response.use(
  function (response) {
    return response
  },
  function (error) {
    // Error block
    const originalRequest = error.config

    if (
      originalRequest?.url?.includes('loyalty/getTier') &&
      error.response?.status === 403
    ) {
      return
    }

    const choppedString: string[] = string_chop(
      zetApi.defaults.headers.common.Authorization?.toString() || '',
      255,
    )

    const eventPayload: any = {
      baseUrl: originalRequest?.baseURL,
      path: originalRequest?.url?.split('?')[0],
      params: originalRequest?.url?.split('?')[1],
      method: originalRequest?.method,
      statusCode: error.response?.status,
      errorCode: error.code,
      duration: error.response?.duration,
      payload: error.response?.config?.data,
      appVersionNo: getAppVersionNo(),
      ...(Platform.OS !== 'web' && { appOS: Platform.OS }),
      authToken:
        (zetApi.defaults.headers.common?.Authorization &&
          zetApi.defaults.headers.common.Authorization?.toString()) ||
        '',
    }

    choppedString.forEach((token, index) => {
      eventPayload[`authToken${index}`] = token
    })

    // if (error.response?.status) {
    //   publishEvent(
    //     error.code === 'ECONNABORTED'
    //       ? 'zet_api_timeout'
    //       : 'zet_api_response_fail',
    //     eventPayload
    //   )
    // } else {
    //   publishEvent('zet_api_response_fail_without_status_code', eventPayload)
    // }

    if (error?.response?.status === 502) {
      logError(error)
      setTimeout(() => resetNavigation('ServerDownScreen'), 500)
    }
    if (error?.response?.status === 504) {
      logError(error)
    }
    if (error?.response?.status === 401 && isApp && getUserToken()) {
      if (!isRefreshTokenFetchTried) {
        callRefreshToken()
      }
    }
    handleUnauthorizedApiError(error, originalRequest)

    throw error
  },
)

const callRefreshToken = async () => {
  isRefreshTokenFetchTried = true
  await refreshToken()
  isRefreshTokenFetchTried = false
}

const handleUnauthorizedApiError = (error: object, originalRequest: object) => {
  const excludedEndpoints = ['']
  if (
    error?.response?.status === 401 &&
    Platform.OS === 'web' &&
    !excludedEndpoints.includes(originalRequest?.url)
  ) {
    useAuthStore.setState({ unauthorizedApiError: true })
  }
}

export const changeLocaleInHeader = (locale = 'en') => {
  zetApi.defaults.headers.common = {
    ...zetApi.defaults.headers.common,
    locale: locale,
  }
}

export const addHeaderHash = (hash: string) => {
  zetApi.defaults.headers.common = {
    ...zetApi.defaults.headers.common,
    hash: hash,
  }
}

export { zetApi }
